import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import mainAxios from '../../services/mainAxios';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'primereact/checkbox';
import Loader from '../Layout/Loader';
import { swalAlert } from '../../services/functions';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';

const useApprovePurchInvoices = ({
  invoices = [],
  user,
  selectedCompany,
  fullCompany,
  allTcr = [],
  getFilteredResults
}) => {
  const { t } = useTranslation('translations');
  const [selectedApproveRows, setSelectedApproveRows] = useState([]);
  const [actionLoadingApprove, setActionLoadingApprove] = useState(false);
  const [openApprovePurchaseWorkflow, setOpenApprovePurchaseWorkflow] = useState(false);
  const [selectedPurchaseUnit, setSelectedPurchaseUnit] = useState();

  const purchaseUnitOptions = useMemo(() =>
    [{ label: 'Select', value: null }].concat(allTcr),
    [allTcr]
  );

  const saveForApprovalPurchase = useCallback(({ status, workflowStatus, action }) => {
    setActionLoadingApprove(true);

    mainAxios.post('endpoint/v2/saveMyPurchaseInvoiceInfoGroup', {
      attachements: [],
      comment: '',
      status,
      workflowStatus,
      action,
      ids: (selectedApproveRows || []).map((s) => s?.id ?? s),
      purchaseUnit: selectedPurchaseUnit,
      nuis: user.companiesRights.find(
        (company) => company.id === parseInt(selectedCompany)
      )?.nuis,
    }).then((res) => {
      setActionLoadingApprove(false);
      if (res.data.status) {
        setSelectedApproveRows([]);
        toast.success(t(['common.actionSuccessfully']), {
          containerId: 'all_toast',
        });
        getFilteredResults({});
        setOpenApprovePurchaseWorkflow(false);
      }
    }).catch(() => {
      setActionLoadingApprove(false);
    });
  }, [selectedApproveRows, selectedPurchaseUnit, user, selectedCompany, t, getFilteredResults]);

  // Workflow 1 - Filter
  const approveMultiInovicesFilterWorkflow1 = useMemo(() => {
    const invoicesforApproval = invoices?.filter(
      (invoice) => invoice?.purchaseStatus === 'DELIVERED' && invoice.eic && invoice?.extracted
    ) ?? [];

    if (invoicesforApproval?.length === 0 || user?.purchaseRole?.id !== 13) return <></>;
    return (
      <Checkbox
        onChange={(e) => {
          if (e.checked) {
            const _selectedRows = [];
            invoicesforApproval.forEach((invoice) => {
              _selectedRows.push({ id: invoice?.id, attachmentId: invoice?.attachmentId });
            });
            setSelectedApproveRows(_selectedRows);
          } else {
            setSelectedApproveRows([]);
          }
        }}
        checked={selectedApproveRows.length === invoicesforApproval?.length && selectedApproveRows.length !== 0}
      />
    );
  }, [invoices, selectedApproveRows, fullCompany?.workflow, user?.purchaseRole]);

  const approveMultiInovicesFilterWorkflowOther = useMemo(() => {
    const invoicesforApproval = invoices?.filter(
      (invoice) =>
        invoice?.internalStatus === 'Draft' &&
        invoice.purchaseStatus === 'DELIVERED' &&
        invoice.id &&
        invoice.extracted &&
        [8].includes(user?.purchaseRole?.id)
    ) ?? [];

    if (invoicesforApproval?.length === 0) return <></>;
    return (
      <Checkbox
        onChange={(e) => {
          if (e.checked) {
            const _selectedRows = [];
            invoicesforApproval.forEach((invoice) => {
              _selectedRows.push({ id: invoice?.id, attachmentId: invoice?.attachmentId });
            });
            setSelectedApproveRows(_selectedRows);
          } else {
            setSelectedApproveRows([]);
          }
        }}
        checked={selectedApproveRows.length === invoicesforApproval?.length && selectedApproveRows.length !== 0}
      />
    );
  }, [invoices, selectedApproveRows,fullCompany?.workflow, user?.purchaseRole]);

  const approveWorkflow1Body = useCallback(
    (invoice) => {
      if (invoice?.purchaseStatus !== 'DELIVERED' || !invoice.eic || user?.purchaseRole?.id !== 13 || !invoice?.extracted) {
        return <></>;
      }
      return (
        <Checkbox
          onChange={(e) => {
            setSelectedApproveRows((state) => {
              const _state = [...state];
              const checkedValueIndex = _state.findIndex((sRow) => sRow.id === invoice?.id);
              if (checkedValueIndex >= 0) {
                _state.splice(checkedValueIndex, 1);
                return _state;
              }
              _state.push({ id: invoice.id, attachmentId: invoice?.attachmentId });
              return _state;
            });
          }}
          checked={!!selectedApproveRows.find((sRow) => sRow.id === invoice?.id)}
        />
      );
    },
    [selectedApproveRows, user?.purchaseRole?.id]
  );

  // Other Workflow - Body
  const approveWorkflowOtherBody = useCallback(
    (invoice) => {
      if (
        invoice?.internalStatus !== 'Draft' ||
        invoice.purchaseStatus !== 'DELIVERED' ||
        !invoice.id ||
        ![8].includes(user?.purchaseRole?.id) ||
        !invoice.extracted
      ) {
        return <></>;
      }
      return (
        <Checkbox
          onChange={(e) => {
            setSelectedApproveRows((state) => {
              const _state = [...state];
              const checkedValueIndex = _state.findIndex((sRow) => sRow.id === invoice?.id);
              if (checkedValueIndex >= 0) {
                _state.splice(checkedValueIndex, 1);
                return _state;
              }
              _state.push({ id: invoice.id, attachmentId: invoice?.attachmentId });
              return _state;
            });
          }}
          checked={!!selectedApproveRows.find((sRow) => sRow.id === invoice?.id)}
        />
      );
    },
    [selectedApproveRows, user?.purchaseRole?.id]
  );

  const approveMultiInovicesFilter =
    fullCompany?.workflow?.id === 1 ? approveMultiInovicesFilterWorkflow1 : approveMultiInovicesFilterWorkflowOther;
  const approveMultiInovicesBody =
    fullCompany?.workflow?.id === 1 ? approveWorkflow1Body : approveWorkflowOtherBody;

  const clearApproveAction = (
    <button
      className={`btn btn-sm btn-outline-primary rounded-pill mr-3 ${fullCompany?.workflow?.id !== 1 ? 'mt-4' : ''}`}
      disabled={actionLoadingApprove}
      onClick={async () => {
        if (actionLoadingApprove) return;
        setSelectedApproveRows([]);
      }}
    >
      {actionLoadingApprove ? <Loader size="xs" /> : t('common.clear')}
    </button>
  );

  const approvePurchasesWorkflow1 = selectedApproveRows.length > 0 && (
    <div className="d-flex align-items-center mt-4">
      <button
        className="btn btn-sm btn-outline-primary rounded-pill mr-3"
        disabled={actionLoadingApprove}
        onClick={async () => {
          if (actionLoadingApprove) return;

          const sentToCisBodyApprove = {
            action: 'Approve CIS',
            status: 'DELIVERED',
            workflowStatus: 'Accepted',
          };

          let res = await swalAlert({ title: t('purchases.approveAllWarning'), t });
          if (res) {
            saveForApprovalPurchase(sentToCisBodyApprove);
          }
        }}
      >
        {actionLoadingApprove ? <Loader size="xs" /> : `${t('purchases.approveAll')} (${selectedApproveRows.length})`}
      </button>
      <button
        className="btn btn-sm btn-outline-primary rounded-pill mr-3"
        disabled={actionLoadingApprove}
        onClick={async () => {
          if (actionLoadingApprove) return;
          let res = await swalAlert({ title: t('purchases.rejectAllWarning'), t });
          if (res) {
            const sentToCisBodyReject = {
              action: 'Reject CIS',
              status: 'DELIVERED',
              workflowStatus: 'Refused',
            };
            saveForApprovalPurchase(sentToCisBodyReject);
          }
        }}
      >
        {actionLoadingApprove ? <Loader size="xs" /> : `${t('purchases.rejectAll')} (${selectedApproveRows.length})`}
      </button>
      {clearApproveAction}
    </div>
  );

  const approvePurchaseWorkflowOther = selectedApproveRows.length > 0 && (
    <>
      <div className="d-flex align-items-center">
        <button
          className="btn btn-sm btn-outline-primary rounded-pill mr-3 mt-4"
          disabled={actionLoadingApprove}
          onClick={async () => {
            if (actionLoadingApprove) return;
            setOpenApprovePurchaseWorkflow(true);
          }}
        >
          {actionLoadingApprove ? <Loader size="xs" /> : `${t('purchases.submitForApproval')} (${selectedApproveRows.length})`}
        </button>
        {clearApproveAction}
      </div>
      <Dialog
        header={t('purchases.submitAllForApprovalWarning')}
        visible={openApprovePurchaseWorkflow}
        onHide={() => setOpenApprovePurchaseWorkflow(false)}
        style={{ width: '590px' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <div className="alert alert-warning" role="alert">
          {t('purchases.purchaseUnitWarnining')}
        </div>
        <label htmlFor="purchaseUnit">{t('purchases.purchaseUnit')}</label>
        <div className="w-full">
          <Dropdown
            style={{ width: '100%' }}
            options={purchaseUnitOptions}
            value={selectedPurchaseUnit}
            isSearchable={true}
            onChange={(purchaseUnit) => {
              setSelectedPurchaseUnit(purchaseUnit.value);
            }}
            isDisabled={![8, 13].includes(user?.purchaseRole?.id)}
            placeholder={t('invoice.select')}
            name="purchaseUnit"
          />
        </div>

        <div className="d-flex justify-content-end mt-3">
          <div
            className="btn btn-sm btn-primary mr-2"
            onClick={async () => {
              const sentForApprovalBody = {
                status: 'DELIVERED',
                workflowStatus: 'Approval',
                action: 'submit for approval',
              };
              saveForApprovalPurchase(sentForApprovalBody);
            }}
          >
            {actionLoadingApprove ? <Loader size="xs" /> : t(['purchases.submitForApproval'])}
          </div>
          <div className="btn btn-sm btn-secondary mr-2" onClick={async () => setOpenApprovePurchaseWorkflow(false)}>
            Close
          </div>
        </div>
      </Dialog>
    </>
  );

  const approvePurchaseButtons =
    fullCompany?.workflow?.id === 1 ? approvePurchasesWorkflow1 : approvePurchaseWorkflowOther;

  return {
    selectedApproveRows,
    setSelectedApproveRows,
    actionLoadingApprove,
    approveMultiInovicesFilter,
    approveMultiInovicesBody,
    approvePurchaseButtons,
    saveForApprovalPurchase,
    openApprovePurchaseWorkflow,
    setOpenApprovePurchaseWorkflow,
    selectedPurchaseUnit,
    setSelectedPurchaseUnit,
    purchaseUnitOptions,
  };
};

export default useApprovePurchInvoices;
